/**
 * Cookie banner — replicated from the reference (bottom-anchored navy card,
 * three actions: Manage / Reject Non-Essential / Accept All).
 * Persistence: localStorage key `mist_cookie_consent` = "all" | "essential".
 */
import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "mist_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const accept = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 sm:inset-x-6 sm:bottom-6 z-40 rounded-md bg-mist-navy text-white shadow-2xl border border-mist-gold/30"
    >
      <div className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="h-9 w-9 rounded-full bg-mist-gold/15 border border-mist-gold/30 grid place-items-center shrink-0">
            <Cookie className="h-4 w-4 text-mist-gold" aria-hidden />
          </div>
          <div className="text-sm text-white/80 leading-relaxed max-w-2xl">
            <div className="text-white font-medium mb-1">We value your privacy</div>
            We use essential cookies to make our site work. With your consent we may also use non-essential cookies to
            improve the experience. You can accept, reject, or manage preferences.
          </div>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          <button
            type="button"
            data-testid="cookie-manage"
            onClick={() => accept("essential")}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 h-9 text-xs uppercase tracking-widest text-white/80 hover:text-white hover:bg-white/10 transition"
          >
            Manage
          </button>
          <button
            type="button"
            data-testid="cookie-reject"
            onClick={() => accept("essential")}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-white/30 bg-transparent px-4 py-2 h-9 text-xs uppercase tracking-widest text-white hover:bg-white/10 transition"
          >
            Reject Non-Essential
          </button>
          <button
            type="button"
            data-testid="cookie-accept"
            onClick={() => accept("all")}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-mist-gold text-mist-navy px-4 py-2 h-9 text-xs uppercase tracking-widest hover:bg-mist-gold/90 transition"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
